/**
 * Build a partial `details` object pre-filled from a client record.
 * Centralizes the logic so the staff Quote form and the Client portal Quote form
 * stay consistent.
 *
 * @param {object} client - Client record (name, surname, birth_date, vehicles, marital_status…)
 * @param {string} type - Policy type (auto/casa/vita/salute/infortuni/viaggio/responsabilita_civile)
 * @returns {object} Partial details to merge into the quote form state
 */
export function prefillQuoteDetails(client, type) {
  if (!client) return {};
  const out = {};

  // 🆕 Feb 2026 — Riconoscimento tipo soggetto: usa subject_type quando presente,
  // altrimenti fallback su presenza di ragione_sociale/partita_iva (record legacy).
  const isCompany = client.subject_type === "company"
    || (client.subject_type !== "private" && !!((client.ragione_sociale || "").toString().trim() || (client.partita_iva || "").toString().trim()));
  out.subject_type = isCompany ? "company" : "private";

  // Common: birth_date used by vita/salute (solo per privati)
  if (!isCompany && client.birth_date) out.birth_date = client.birth_date;

  if (isCompany) {
    // === AZIENDA ===
    // 🆕 Feb 2026 — Usa naming coerente con owner_* (owner_ragione_sociale, owner_partita_iva)
    if (client.ragione_sociale) out.owner_ragione_sociale = String(client.ragione_sociale).toUpperCase();
    if (client.partita_iva) out.owner_partita_iva = String(client.partita_iva).toUpperCase();
    if (client.sdi_code) out.sdi_code = String(client.sdi_code).toUpperCase();
    if (client.pec_email) out.pec_email = client.pec_email;
    if (client.codice_fiscale_societario) out.codice_fiscale_societario = String(client.codice_fiscale_societario).toUpperCase();
    // Contatti / sede legale (usati per intestatario nel PDF e comunicazioni)
    if (client.address) out.owner_address = client.address;
    if (client.house_number) out.owner_house_number = client.house_number;
    if (client.city) out.owner_city = client.city;
    if (client.province) out.owner_province = String(client.province).toUpperCase();
    if (client.cap) out.owner_cap = client.cap;
    if (client.email) out.owner_email = client.email;
    if (client.mobile || client.phone) out.owner_phone = client.mobile || client.phone;

    // Legale Rappresentante / Firmatario
    if (client.legale_rappresentante_name) out.legale_rappresentante_name = String(client.legale_rappresentante_name).toUpperCase();
    if (client.legale_rappresentante_surname) out.legale_rappresentante_surname = String(client.legale_rappresentante_surname).toUpperCase();
    if (client.legale_rappresentante_fiscal_code) out.legale_rappresentante_fiscal_code = String(client.legale_rappresentante_fiscal_code).toUpperCase();
    if (client.legale_rappresentante_gender) out.legale_rappresentante_gender = String(client.legale_rappresentante_gender).toUpperCase();
    if (client.legale_rappresentante_birth_date) out.legale_rappresentante_birth_date = client.legale_rappresentante_birth_date;
    if (client.legale_rappresentante_birth_place) out.legale_rappresentante_birth_place = String(client.legale_rappresentante_birth_place).toUpperCase();
    if (client.legale_rappresentante_birth_province) out.legale_rappresentante_birth_province = String(client.legale_rappresentante_birth_province).toUpperCase();
    if (client.legale_rappresentante_role) out.legale_rappresentante_role = client.legale_rappresentante_role;
    if (client.legale_rappresentante_nationality) out.legale_rappresentante_nationality = String(client.legale_rappresentante_nationality).toUpperCase();
    if (client.legale_rappresentante_email) out.legale_rappresentante_email = client.legale_rappresentante_email;
    if (client.legale_rappresentante_mobile) out.legale_rappresentante_mobile = client.legale_rappresentante_mobile;
  } else {
    // === PERSONA FISICA ===
    if (client.name) out.owner_name = client.name;
    if (client.surname) out.owner_surname = client.surname;
    if (client.fiscal_code) out.owner_fiscal_code = String(client.fiscal_code).toUpperCase();
    if (client.birth_date) out.owner_birth_date = client.birth_date;
    if (client.birth_place) out.owner_birth_place = client.birth_place;
    if (client.gender) out.owner_gender = String(client.gender).toUpperCase();
    if (client.marital_status) out.owner_marital_status = client.marital_status;
    if (client.address) out.owner_address = client.address;
    if (client.house_number) out.owner_house_number = client.house_number;
    if (client.city) out.owner_city = client.city;
    if (client.province) out.owner_province = String(client.province).toUpperCase();
    if (client.cap) out.owner_cap = client.cap;
    if (client.email) out.owner_email = client.email;
    if (client.mobile || client.phone) out.owner_phone = client.mobile || client.phone;
  }

  if (type === "auto") {
    // Pre-fill from the client's first vehicle (most recent on top)
    const v = (client.vehicles || [])[0];
    if (v) {
      if (v.plate) out.vehicle_plate = v.plate;
      if (v.brand) out.vehicle_brand = v.brand;
      if (v.model) out.vehicle_model = v.model;
      if (v.year) out.vehicle_year = String(v.year);
    }
    // Driver fields from the client himself (solo per privati)
    if (!isCompany && client.profession) out.profession = client.profession;
  }

  if (type === "casa") {
    // Use the client's residence as default property address suggestion
    const parts = [client.address, client.house_number, client.cap, client.city]
      .filter(Boolean)
      .join(" ");
    if (parts) out.property_address = parts;
  }

  return out;
}
